import React, { useState } from 'react';
import { MapPin, Phone, Mail, CheckCircle } from 'lucide-react';
import { useInView } from '../hooks/useInView';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_INFO = [
  { icon: MapPin, label: 'Address', value: 'C-188, Sector-63, NOIDA, Uttar Pradesh - 201301, India' },
  { icon: Phone, label: 'Phone', value: '+91-120-4276705 / 06' },
  { icon: Mail, label: 'Email', value: 'sales@vimalplastics.com' },
];

const SUBJECTS = [
  'Product Enquiry — Bottles',
  'Product Enquiry — Jars',
  'Product Enquiry — Pumps & Dispensers',
  'Product Enquiry — Caps & Closures',
  'Product Enquiry — Colour Cosmetics',
  'Custom Mould Making',
  'Decoration Services',
  'Printing Services',
  'General Enquiry',
];

const INITIAL_FORM = { name: '', email: '', company: '', phone: '', subject: '', message: '' };

export default function Contact() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [ref, inView] = useInView();

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(INITIAL_FORM);
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or email us directly.');
    }
  };

  return (
    <main style={{ paddingTop: '80px', backgroundColor: '#FAF9F6', minHeight: '100vh' }}>
      {/* Banner */}
      <section className="py-20" style={{ backgroundColor: '#0B1B3D' }} data-testid="contact-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#D4AF37' }}>Get in Touch</p>
          <h1 className="font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
            Contact Us
          </h1>
          <p className="text-base font-light mt-4 max-w-lg" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Share your packaging needs with us and our experts will get back to you within 24 hours.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-14">
            {/* Contact Form */}
            <div
              className="md:col-span-2"
              ref={ref}
              style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(2rem)', transition: 'all 0.7s ease' }}
            >
              <h2 className="font-light mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: '1.75rem' }}>
                Send an Enquiry
              </h2>

              {status === 'success' ? (
                <div className="py-16 text-center" data-testid="contact-success">
                  <CheckCircle size={48} className="mx-auto mb-4" style={{ color: '#D4AF37' }} />
                  <h3 className="font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: '1.5rem' }}>
                    Enquiry Received
                  </h3>
                  <p className="font-light" style={{ color: '#4A4F5A' }}>
                    Thank you for reaching out. Our team will get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 text-xs tracking-[0.15em] uppercase transition-colors"
                    style={{ color: '#D4AF37' }}
                    data-testid="contact-send-another"
                  >
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField label="Full Name *" name="name" value={form.name} onChange={handleChange} required placeholder="Your full name" />
                    <FormField label="Email Address *" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@company.com" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FormField label="Company Name *" name="company" value={form.company} onChange={handleChange} required placeholder="Your company" />
                    <FormField label="Phone Number" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+91 98XXX XXXXX" />
                  </div>
                  <div>
                    <label className="block text-xs tracking-[0.12em] uppercase mb-2 font-medium" style={{ color: '#0B1B3D' }}>Subject *</label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 text-sm font-light bg-white focus:outline-none"
                      style={{ border: '1px solid rgba(11,27,61,0.18)', color: form.subject ? '#1A1D24' : '#9ca3af' }}
                      data-testid="contact-subject"
                    >
                      <option value="">Select an enquiry type</option>
                      {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs tracking-[0.12em] uppercase mb-2 font-medium" style={{ color: '#0B1B3D' }}>Message *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us about your packaging requirements, quantities, deadlines..."
                      className="w-full px-4 py-3 text-sm font-light bg-white focus:outline-none resize-none"
                      style={{ border: '1px solid rgba(11,27,61,0.18)', color: '#1A1D24' }}
                      data-testid="contact-message"
                    />
                  </div>
                  {status === 'error' && (
                    <p className="text-sm" style={{ color: '#c0392b' }} data-testid="contact-error">{errorMsg}</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="px-10 py-4 text-xs tracking-[0.2em] uppercase text-white transition-all hover:opacity-85 disabled:opacity-60"
                    style={{ backgroundColor: '#0B1B3D' }}
                    data-testid="contact-submit-button"
                  >
                    {status === 'loading' ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(2rem)',
                transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
              }}
            >
              <h2 className="font-light mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: '1.75rem' }}>
                Our Details
              </h2>
              <ul className="space-y-6">
                {CONTACT_INFO.map(({ icon: Icon, label, value }) => (
                  <li key={label} className="flex gap-4" data-testid={`contact-info-${label.toLowerCase()}`}>
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F0EDE6' }}>
                      <Icon size={16} style={{ color: '#D4AF37' }} />
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.18em] uppercase mb-1" style={{ color: '#4A4F5A' }}>{label}</p>
                      <p className="text-sm font-light" style={{ color: '#1A1D24' }}>{value}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-10 p-6" style={{ backgroundColor: '#0B1B3D' }}>
                <p className="text-xs tracking-[0.15em] uppercase mb-3" style={{ color: '#D4AF37' }}>Business Hours</p>
                <p className="text-sm text-white font-light mb-1">Monday – Saturday</p>
                <p className="text-sm font-light mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>9:00 AM – 6:00 PM IST</p>
                <p className="text-sm text-white font-light mb-1">Sunday</p>
                <p className="text-sm font-light" style={{ color: 'rgba(255,255,255,0.6)' }}>Closed</p>
              </div>

              <div className="mt-6 p-5" style={{ backgroundColor: '#F0EDE6', borderLeft: '3px solid #D4AF37' }}>
                <p className="text-sm font-light leading-relaxed" style={{ color: '#1A1D24' }}>
                  We typically respond to all enquiries within <strong>24 business hours</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function FormField({ label, name, type = 'text', value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.12em] uppercase mb-2 font-medium" style={{ color: '#0B1B3D' }}>
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full px-4 py-3 text-sm font-light bg-white focus:outline-none"
        style={{ border: '1px solid rgba(11,27,61,0.18)', color: '#1A1D24' }}
        data-testid={`contact-${name}`}
      />
    </div>
  );
}
